// Pure HTML renderer for the SAA confirmation + review emails. Takes a plain view model + a resolved
// brand and builds table-based, inline-styled markup (email-client safe). Styling comes from the token
// helpers in saaEmailTheme; all copy from saaEmailCopy. The review variant shares the design and most
// sections, swaps the copy and adds the "Ja"-answered acceptance questions + akkoordverklaringen.

import {
  type SaaEmailBrand,
  type SaaEmailLogo,
  type SaaEmailTheme,
  TEXT,
  GUTTER,
  RADIUS,
  ON_DARK,
  ON_DARK_MUTED,
  ON_DARK_FAINT,
  FONT,
  css,
  font,
  contentCell,
  escapeHtml,
} from './saaEmailTheme';
import { getEmailCopy, type EmailCopy, type Lang } from './saaEmailCopy';

export interface SaaEmailLine {
  label: string;
  value: string;
}

export interface SaaEmailProduct {
  naam: string;
  base: string; // fixed base-coverage label (All Risk / WA-variant / Consument & Wonen), '' if none
  specs: string[]; // data-backed dekkingsregels (verzekerde som, dekkingsgebied) + chosen options (eigen risico)
  extras: string[]; // selected optional coverages (with insured sum where relevant), rendered with a "+" prefix
}

export interface SaaEmailContact {
  telefoon: string;
  telefoonRaw: string;
  email: string;
  openingstijden: string;
}

// Extra data for the review variant: the "Ja"-answered acceptance questions (the reason the
// request needs manual review) and the accepted declaration labels. Presence of `review` on the
// view model switches copy details shared renderers care about (e.g. "Gewenste ingangsdatum").
export interface SaaReviewQuestion {
  vraag: string;
  toelichting?: string;
}

export interface SaaReviewData {
  vragen: SaaReviewQuestion[];
  akkoorden: string[];
}

export interface SaaConfirmationViewModel {
  lang: Lang;
  voornaam: string;
  startDate: string;
  products: SaaEmailProduct[];
  premieRegels: SaaEmailLine[];
  totalePremie: string;
  betaalwijze: string;
  klant: {
    naam: string;
    adres: string;
    geboortedatum: string;
    email: string;
    telefoon: string;
  };
  contact: SaaEmailContact;
  review?: SaaReviewData;
}

// Copy set for this mail: language from the view model, tone (u/uw vs je/jij) from the brand.
const copyFor = (vm: SaaConfirmationViewModel, brand: SaaEmailBrand): EmailCopy => getEmailCopy(vm.lang, brand.informal);

const TABLE = 'role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"';

const section = (inner: string, top: number, bottom = 0): string =>
  `<tr><td style="${contentCell(top, bottom)}">${inner}</td></tr>`;

const heading = (t: SaaEmailTheme, text: string): string =>
  `<h2 style="${css('margin:0 0 14px 0', font(TEXT.h2, t.primary, 'bold'))}">${escapeHtml(text)}</h2>`;

const bulletList = (t: SaaEmailTheme, items: string[]): string => {
  const rows = items
    .map(
      (item) =>
        `<tr><td valign="top" width="24" style="${css('padding:4px 0', font(TEXT.base, t.accent))}">&bull;</td>` +
        `<td style="${css('padding:4px 0', font(TEXT.base, t.textMuted))}">${escapeHtml(item)}</td></tr>`
    )
    .join('');
  return `<table ${TABLE}>${rows}</table>`;
};

const logoBar = (brand: SaaEmailBrand, scale: number, fallbackColor: string): string => {
  if (!brand.logos.length) {
    return `<span style="${css(`font-size:${Math.round(22 * scale)}px`, 'font-weight:bold', `color:${fallbackColor}`)}">${escapeHtml(brand.fallbackText)}</span>`;
  }
  // width AND height are set explicitly — email clients collapse height:auto to a thin sliver.
  const img = (logo: SaaEmailLogo, align: 'left' | 'right') => {
    const w = Math.round(logo.width * scale);
    const h = Math.round(logo.height * scale);
    return `<img src="${logo.src}" alt="${escapeHtml(logo.alt)}" width="${w}" height="${h}" style="${css('display:block', `width:${w}px`, `height:${h}px`, 'border:0', align === 'right' && 'margin-left:auto')}" />`;
  };
  const left = brand.logos[0];
  const right = brand.logos.length > 1 ? brand.logos[brand.logos.length - 1] : null;
  return `<table ${TABLE}><tr><td align="left" valign="middle">${img(left, 'left')}</td>${right ? `<td align="right" valign="middle">${img(right, 'right')}</td>` : ''}</tr></table>`;
};

const renderHeader = (_vm: SaaConfirmationViewModel, brand: SaaEmailBrand): string => {
  const t = brand.theme;
  return (
    `<tr><td style="${css(`background-color:${t.headerBg}`, `padding:22px ${GUTTER}px`)}" valign="middle">${logoBar(brand, 1, t.primary)}</td></tr>` +
    `<tr><td style="${css('height:4px', `background-color:${t.primary}`, 'font-size:0', 'line-height:0')}">&nbsp;</td></tr>`
  );
};

const renderOpening = (vm: SaaConfirmationViewModel, brand: SaaEmailBrand): string => {
  const t = brand.theme;
  const c = copyFor(vm, brand);
  return section(
    `<h1 class="h1" style="${css('margin:0 0 16px 0', font(TEXT.h1, t.primary, 'bold'))}">${escapeHtml(c.title)} ${escapeHtml(brand.packageName)}</h1>` +
      `<p style="${css('margin:0 0 14px 0', font(TEXT.lead, t.primary))}">${escapeHtml(c.greeting(vm.voornaam))}</p>` +
      `<p style="${css('margin:0', font(TEXT.body, t.textMuted))}">${escapeHtml(c.intro(brand.packageName, brand.signoffName))}</p>`,
    36,
    8
  );
};

const renderPakket = (vm: SaaConfirmationViewModel, brand: SaaEmailBrand): string => {
  const t = brand.theme;
  const c = copyFor(vm, brand);
  // In review mode the start date is not guaranteed yet -> "Gewenste ingangsdatum".
  const ingangsdatumLabel = vm.review ? c.review.ingangsdatum : c.ingangsdatum;
  const header =
    `<table ${TABLE} style="margin-bottom:14px;"><tr>` +
    `<td valign="bottom" style="${font(TEXT.h2, t.primary, 'bold')}">${escapeHtml(c.pakketHeading(brand.packageName))}</td>` +
    `<td align="right" valign="bottom" style="${css(font(TEXT.meta, t.textMuted), 'white-space:nowrap')}">${escapeHtml(ingangsdatumLabel)}: ${escapeHtml(vm.startDate)}</td>` +
    `</tr></table>`;
  const subLine = (text: string) => `<p style="${css('margin:2px 0 0 0', font(TEXT.base, t.textMuted))}">${text}</p>`;
  const items = vm.products
    .map((p) => {
      const base = p.base ? subLine(escapeHtml(p.base)) : '';
      const specs = p.specs.map((s) => subLine(escapeHtml(s))).join('');
      const extras = p.extras.map((x) => subLine(`+ ${escapeHtml(x)}`)).join('');
      const sub = base + specs + extras;
      return (
        `<table ${TABLE} style="margin-bottom:14px;">` +
        `<tr><td style="${font(TEXT.total, t.primary, 'bold')}">${escapeHtml(p.naam)}</td></tr>` +
        (sub ? `<tr><td>${sub}</td></tr>` : '') +
        `</table>`
      );
    })
    .join('');
  return section(header + items, 28, 4);
};

const renderPremie = (vm: SaaConfirmationViewModel, brand: SaaEmailBrand): string => {
  const t = brand.theme;
  const c = copyFor(vm, brand);
  const rowPad = 'padding:12px 18px 0 18px';
  const rows = vm.premieRegels
    .map(
      (r) =>
        `<tr><td style="${css(rowPad, font(TEXT.row, t.textMuted))}">${escapeHtml(r.label)}</td>` +
        `<td align="right" style="${css(rowPad, font(TEXT.row, t.textMuted), 'white-space:nowrap')}">${escapeHtml(r.value)}</td></tr>`
    )
    .join('');
  const divider = `<tr><td colspan="2" style="${rowPad};"><table ${TABLE}><tr><td style="${css(`border-top:1px solid ${t.line}`, 'font-size:0', 'line-height:0', 'height:1px')}">&nbsp;</td></tr></table></td></tr>`;
  const totalPad = 'padding:12px 18px 14px 18px';
  const totalRow =
    `<tr><td style="${css(totalPad, font(TEXT.total, t.primary, 'bold'))}">${escapeHtml(c.totaal)}</td>` +
    `<td align="right" style="${css(totalPad, font(TEXT.total, t.primary, 'bold'), 'white-space:nowrap')}">&euro; ${escapeHtml(vm.totalePremie)} ${escapeHtml(c.perMaand)}</td></tr>`;

  return (
    `<tr><td style="${contentCell(24)}">${heading(t, c.premieHeading)}</td></tr>` +
    `<tr><td style="${css(`padding:0 ${GUTTER}px 8px ${GUTTER}px`)}">` +
    `<table ${TABLE} style="${css(`background-color:${t.pageBg}`, `border-radius:${RADIUS}px`)}">${rows}${divider}${totalRow}</table>` +
    `<p style="${css('margin:12px 0 0 0', font(TEXT.small, t.textMuted))}">${escapeHtml(c.betaalwijze)}: ${escapeHtml(vm.betaalwijze)}</p>` +
    `</td></tr>`
  );
};

// Uw-gegevens rows as data; `html: true` keeps a pre-formatted value (adres has <br>) unescaped.
const GEGEVENS_FIELDS: Array<{
  labelKey: 'labelNaam' | 'labelAdres' | 'labelGeboortedatum' | 'labelEmail' | 'labelTelefoon';
  get: (k: SaaConfirmationViewModel['klant']) => string;
  html?: boolean;
}> = [
  { labelKey: 'labelNaam', get: (k) => k.naam },
  { labelKey: 'labelAdres', get: (k) => k.adres, html: true },
  { labelKey: 'labelGeboortedatum', get: (k) => k.geboortedatum },
  { labelKey: 'labelEmail', get: (k) => k.email },
  { labelKey: 'labelTelefoon', get: (k) => k.telefoon },
];

const renderGegevens = (vm: SaaConfirmationViewModel, brand: SaaEmailBrand): string => {
  const t = brand.theme;
  const c = copyFor(vm, brand);
  const rows = GEGEVENS_FIELDS.map((f) => {
    const value = f.html ? f.get(vm.klant) : escapeHtml(f.get(vm.klant));
    return (
      `<tr><td width="38%" valign="top" style="${css('padding:6px 0', font(TEXT.base, t.textMuted))}">${escapeHtml(c[f.labelKey])}</td>` +
      `<td valign="top" style="${css('padding:6px 0', font(TEXT.base, t.primary))}">${value}</td></tr>`
    );
  }).join('');
  return section(
    heading(t, c.gegevensHeading) +
      `<table ${TABLE}>${rows}</table>` +
      `<p style="${css('margin:10px 0 0 0', font(TEXT.base, t.textMuted))}">${escapeHtml(c.gegevensFooter)}</p>`,
    28
  );
};

// "heading + bullet list" sections differ only by copy + top padding -> one factory.
const bulletSection =
  (headingKey: 'watNuHeading' | 'wistuHeading', itemsKey: 'watNu' | 'wistu', top: number) =>
  (vm: SaaConfirmationViewModel, brand: SaaEmailBrand): string =>
    section(heading(brand.theme, copyFor(vm, brand)[headingKey]) + bulletList(brand.theme, copyFor(vm, brand)[itemsKey]), top);

// ---- Review-only sections (aanvraag onder handmatige beoordeling) ----

const renderReviewOpening = (vm: SaaConfirmationViewModel, brand: SaaEmailBrand): string => {
  const t = brand.theme;
  const c = copyFor(vm, brand);
  return section(
    `<h1 class="h1" style="${css('margin:0 0 16px 0', font(TEXT.h1, t.primary, 'bold'))}">${escapeHtml(c.review.title)}</h1>` +
      `<p style="${css('margin:0 0 14px 0', font(TEXT.lead, t.primary))}">${escapeHtml(c.greeting(vm.voornaam))}</p>` +
      `<p style="${css('margin:0', font(TEXT.body, t.textMuted))}">${escapeHtml(c.review.intro(brand.packageName, brand.signoffName))}</p>`,
    36,
    8
  );
};

// The vragen/akkoorden cards reuse the premie card's look: pageBg, rounded corners, 18px side
// padding, hairline dividers between rows. Two-column rows -> dividers/spacers span both columns.
const reviewDivider = (t: SaaEmailTheme): string =>
  `<tr><td colspan="2" style="padding:12px 18px 0 18px;"><table ${TABLE}><tr><td style="${css(`border-top:1px solid ${t.line}`, 'font-size:0', 'line-height:0', 'height:1px')}">&nbsp;</td></tr></table></td></tr>`;

const reviewCardSpacer = `<tr><td colspan="2" style="${css('font-size:0', 'line-height:0', 'height:14px')}">&nbsp;</td></tr>`;

const reviewCard = (t: SaaEmailTheme, rows: string): string =>
  `<table ${TABLE} style="${css(`background-color:${t.pageBg}`, `border-radius:${RADIUS}px`)}">${rows}${reviewCardSpacer}</table>`;

const renderReviewVragen = (vm: SaaConfirmationViewModel, brand: SaaEmailBrand): string => {
  if (!vm.review?.vragen.length) return '';
  const t = brand.theme;
  const c = copyFor(vm, brand).review;
  const rows = vm.review.vragen
    .map((q, i) => {
      const toelichting = q.toelichting
        ? `<tr><td colspan="2" style="${css('padding:4px 18px 0 18px', font(TEXT.small, t.textMuted), 'font-style:italic')}">${escapeHtml(c.toelichtingLabel)}: ${escapeHtml(q.toelichting)}</td></tr>`
        : '';
      return (
        (i > 0 ? reviewDivider(t) : '') +
        `<tr><td valign="top" style="${css('padding:12px 0 0 18px', font(TEXT.row, t.textMuted))}">${escapeHtml(q.vraag)}</td>` +
        `<td align="right" valign="top" style="${css('padding:12px 18px 0 12px', font(TEXT.row, t.primary, 'bold'), 'white-space:nowrap')}">${escapeHtml(c.answerYes)}</td></tr>` +
        toelichting
      );
    })
    .join('');
  return section(
    heading(t, c.vragenHeading) +
      `<p style="${css('margin:0 0 12px 0', font(TEXT.base, t.textMuted))}">${escapeHtml(c.vragenIntro)}</p>` +
      reviewCard(t, rows),
    28
  );
};

const renderReviewAkkoorden = (vm: SaaConfirmationViewModel, brand: SaaEmailBrand): string => {
  if (!vm.review?.akkoorden.length) return '';
  const t = brand.theme;
  const c = copyFor(vm, brand).review;
  const rows = vm.review.akkoorden
    .map(
      (label, i) =>
        (i > 0 ? reviewDivider(t) : '') +
        `<tr><td valign="top" style="${css('padding:12px 0 0 18px', font(TEXT.row, t.textMuted))}">${escapeHtml(label)}</td>` +
        `<td align="right" valign="top" style="${css('padding:12px 18px 0 12px', font(TEXT.row, t.primary, 'bold'), 'white-space:nowrap')}">&#10003; ${escapeHtml(c.akkoordLabel)}</td></tr>`
    )
    .join('');
  return section(heading(t, c.akkoordHeading) + reviewCard(t, rows), 28);
};

const renderReviewWatNu = (vm: SaaConfirmationViewModel, brand: SaaEmailBrand): string =>
  section(
    heading(brand.theme, copyFor(vm, brand).review.watNuHeading) + bulletList(brand.theme, copyFor(vm, brand).review.watNu),
    28
  );

const renderReviewAfsluiting = (vm: SaaConfirmationViewModel, brand: SaaEmailBrand): string => {
  const t = brand.theme;
  const c = copyFor(vm, brand);
  return section(
    `<p style="${css('margin:0 0 18px 0', font(TEXT.body, t.textMuted))}">${escapeHtml(c.review.afsluiting(brand.trustName))}</p>` +
      `<p style="${css('margin:0', font(TEXT.body, t.primary))}">${escapeHtml(c.groet)}<br /><strong>${escapeHtml(brand.signoffName)}</strong></p>`,
    28,
    8
  );
};

const renderContact = (vm: SaaConfirmationViewModel, brand: SaaEmailBrand): string => {
  const t = brand.theme;
  const c = copyFor(vm, brand);
  const k = vm.contact;
  const line = (label: string, value: string) =>
    `<tr><td width="120" valign="top" style="${font(TEXT.meta, ON_DARK_FAINT)}">${escapeHtml(label)}</td>` +
    `<td valign="top" style="${font(TEXT.meta, ON_DARK)}">${value}</td></tr>`;
  const link = (href: string, text: string) => `<a href="${href}" style="${css(`color:${ON_DARK}`, 'text-decoration:none')}">${escapeHtml(text)}</a>`;
  const rows =
    (k.telefoon ? line(c.contactTelefoon, link(`tel:${escapeHtml(k.telefoonRaw)}`, k.telefoon)) : '') +
    (k.email ? line(c.contactEmail, link(`mailto:${escapeHtml(k.email)}`, k.email)) : '') +
    (k.openingstijden ? line(c.contactOpeningstijden, escapeHtml(k.openingstijden)) : '');
  return section(
    `<table ${TABLE} style="${css(`background-color:${t.primary}`, `border-radius:${RADIUS}px`)}"><tr>` +
      `<td style="${css('padding:22px 24px', `font-family:${FONT}`)}">` +
      `<h2 style="${css('margin:0 0 8px 0', font(TEXT.cardTitle, ON_DARK, 'bold'))}">${escapeHtml(c.contactHeading)}</h2>` +
      `<p style="${css('margin:0 0 14px 0', font(TEXT.base, ON_DARK_MUTED))}">${escapeHtml(c.contactIntro)}</p>` +
      `<table ${TABLE}>${rows}</table>` +
      `</td></tr></table>`,
    28
  );
};

const renderAfsluiting = (vm: SaaConfirmationViewModel, brand: SaaEmailBrand): string => {
  const t = brand.theme;
  const c = copyFor(vm, brand);
  return section(
    `<p style="${css('margin:0 0 18px 0', font(TEXT.body, t.textMuted))}">${escapeHtml(c.afsluiting(brand.trustName))}</p>` +
      `<p style="${css('margin:0', font(TEXT.body, t.primary))}">${escapeHtml(c.groet)}<br /><strong>${escapeHtml(brand.signoffName)}</strong></p>`,
    28,
    8
  );
};

const renderFooter = (vm: SaaConfirmationViewModel, brand: SaaEmailBrand): string => {
  const t = brand.theme;
  const c = copyFor(vm, brand);
  return (
    `<tr><td style="${css(`padding:28px ${GUTTER}px`, `background-color:${t.pageBg}`, `border-top:1px solid ${t.line}`, `font-family:${FONT}`)}">` +
    logoBar(brand, 0.72, t.footerText) +
    `<p style="${css('margin:16px 0 0 0', font(TEXT.fine, t.footerText))}">${escapeHtml(c.disclaimer)}</p>` +
    `</td></tr>`
  );
};

// Ordered section pipelines — reorder/add/remove the emails' sections here in one place.
type SectionRenderer = (vm: SaaConfirmationViewModel, brand: SaaEmailBrand) => string;

const SECTIONS: SectionRenderer[] = [
  renderHeader,
  renderOpening,
  renderPakket,
  renderPremie,
  renderGegevens,
  bulletSection('watNuHeading', 'watNu', 28),
  renderContact,
  bulletSection('wistuHeading', 'wistu', 24),
  renderAfsluiting,
  renderFooter,
];

// Review variant: same design, review copy. Adds the "Ja"-answered vragen + akkoordverklaringen
// and drops the promotional "Wist u dat…" block (a review mail keeps a more sober tone).
const REVIEW_SECTIONS: SectionRenderer[] = [
  renderHeader,
  renderReviewOpening,
  renderPakket,
  renderPremie,
  renderGegevens,
  renderReviewVragen,
  renderReviewAkkoorden,
  renderReviewWatNu,
  renderContact,
  renderReviewAfsluiting,
  renderFooter,
];

export function buildSaaConfirmationEmailHtml(vm: SaaConfirmationViewModel, brand: SaaEmailBrand): string {
  return buildEmailShell(vm, brand, SECTIONS, copyFor(vm, brand).subject);
}

export function buildSaaReviewEmailHtml(vm: SaaConfirmationViewModel, brand: SaaEmailBrand): string {
  return buildEmailShell(vm, brand, REVIEW_SECTIONS, copyFor(vm, brand).review.subject);
}

function buildEmailShell(vm: SaaConfirmationViewModel, brand: SaaEmailBrand, renderers: SectionRenderer[], title: string): string {
  const t = brand.theme;
  const sections = renderers.map((render) => render(vm, brand)).join('');

  return `<!DOCTYPE html PUBLIC "-//W3C//DTD XHTML 1.0 Transitional//EN" "http://www.w3.org/TR/xhtml1/DTD/xhtml1-transitional.dtd">
<html xmlns="http://www.w3.org/1999/xhtml" lang="${vm.lang}">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <meta http-equiv="X-UA-Compatible" content="IE=edge" />
  <meta name="x-apple-disable-message-reformatting" />
  <meta name="color-scheme" content="light dark" />
  <meta name="supported-color-schemes" content="light dark" />
  <title>${escapeHtml(title)}</title>
  <style type="text/css">
    body { margin:0; padding:0; width:100% !important; }
    table { border-collapse:collapse; }
    img { border:0; line-height:100%; outline:none; text-decoration:none; -ms-interpolation-mode:bicubic; }
    @media only screen and (max-width:620px) {
      .container { width:100% !important; }
      .h1 { font-size:22px !important; line-height:28px !important; }
    }
  </style>
</head>
<body style="margin:0; padding:0; background-color:${t.pageBg};">
  <table ${TABLE} style="background-color:${t.pageBg};">
    <tr>
      <td align="center" style="padding:24px 12px;">
        <table role="presentation" class="container" width="600" cellpadding="0" cellspacing="0" border="0" style="width:600px; max-width:600px; background-color:${t.cardBg}; border-radius:${RADIUS}px; overflow:hidden;">
          ${sections}
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
}
